/**
 * Team Cards — Frontend interaction
 *
 * Behaviour:
 * - Clicking a card expands a bio panel inserted AFTER the last card in that
 *   visual row (so it always appears below the clicked card's row).
 * - Clicking the same card again, or the × button, closes it.
 * - Only one bio is open at a time per block.
 * - If showBioDefault is set, the first card's bio opens on page load.
 */

( function () {
    'use strict';

    /**
     * Return the column count of the grid at the current viewport width
     * by inspecting computed column positions of the cards.
     */
    function getColumnsPerRow( grid ) {
        const cards = Array.from( grid.querySelectorAll( '.tc-card' ) );
        if ( cards.length < 2 ) return cards.length;
        const firstTop = cards[ 0 ].getBoundingClientRect().top;
        let cols = 1;
        for ( let i = 1; i < cards.length; i++ ) {
            if ( Math.abs( cards[ i ].getBoundingClientRect().top - firstTop ) < 4 ) {
                cols++;
            } else {
                break;
            }
        }
        return cols;
    }

    /**
     * Given a clicked card index and total columns, return the index of the
     * LAST card in the same row.
     */
    function lastIndexInRow( clickedIndex, cols, totalCards ) {
        const rowStart  = Math.floor( clickedIndex / cols ) * cols;
        const rowEnd    = Math.min( rowStart + cols - 1, totalCards - 1 );
        return rowEnd;
    }

    function initBlock( block ) {
        const grid       = block.querySelector( '.tc-grid' );
        const bioPanel   = block.querySelector( '.tc-bio-panel' );
        const closeBtn   = block.querySelector( '.tc-bio-close' );
        const dataEl     = block.querySelector( '.tc-data' );
        const showDefault = block.dataset.showBioDefault === '1';

        if ( ! grid || ! bioPanel || ! dataEl ) return;

        let members;
        try {
            members = JSON.parse( dataEl.textContent );
        } catch ( e ) {
            return;
        }

        const cards = Array.from( grid.querySelectorAll( '.tc-card--has-bio' ) );
        let activeIndex = null;

        // ── Populate the bio panel with a member's data ────────────────
        function populatePanel( index ) {
            const m = members[ index ];
            if ( ! m ) return;

            const photoEl = bioPanel.querySelector( '.tc-bio-photo' );
            if ( photoEl ) {
                if ( m.photo ) {
                    photoEl.src  = m.photo;
                    photoEl.alt  = m.name;
                    photoEl.style.display = '';
                } else {
                    photoEl.style.display = 'none';
                }
            }

            const nameEl = bioPanel.querySelector( '.tc-bio-name' );
            if ( nameEl ) nameEl.textContent = m.name;

            const titleEl = bioPanel.querySelector( '.tc-bio-title' );
            if ( titleEl ) {
                titleEl.textContent  = m.title;
                titleEl.style.display = m.showTitle ? '' : 'none';
            }

            const bodyEl = bioPanel.querySelector( '.tc-bio-body' );
            if ( bodyEl ) bodyEl.innerHTML = m.bio;
        }

        // ── Insert the panel after the last card in the clicked row ────
        function insertPanelAfterRow( clickedIndex ) {
            const allCards  = Array.from( grid.querySelectorAll( '.tc-card' ) );
            const cols      = getColumnsPerRow( grid );
            const lastIdx   = lastIndexInRow( clickedIndex, cols, allCards.length );
            const lastCard  = allCards[ lastIdx ];

            // If panel is already after the right card, no DOM move needed.
            if ( bioPanel.previousElementSibling === lastCard ) return;

            // Insert panel immediately after lastCard — grid handles layout.
            lastCard.after( bioPanel );
        }

        // ── Open a bio panel ───────────────────────────────────────────
        // Pass silent=true to skip scrollIntoView (used on page load so the
        // browser doesn't jump down to the bio panel automatically).
        function openCard( index, silent = false ) {
            // Deactivate previously active card (if any)
            if ( activeIndex !== null && activeIndex !== index ) {
                const prev = grid.querySelector( `.tc-card[data-index="${ activeIndex }"]` );
                if ( prev ) {
                    prev.classList.remove( 'tc-card--active' );
                    prev.setAttribute( 'aria-expanded', 'false' );
                }
            }

            activeIndex = index;
            const card  = grid.querySelector( `.tc-card[data-index="${ index }"]` );

            card.classList.add( 'tc-card--active' );
            card.setAttribute( 'aria-expanded', 'true' );

            populatePanel( index );
            insertPanelAfterRow( index );
            bioPanel.removeAttribute( 'hidden' );

            // Only scroll when the user explicitly clicked — not on initial load.
            if ( ! silent ) {
                bioPanel.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
            }
        }

        // ── Close the bio panel ────────────────────────────────────────
        function closePanel() {
            if ( activeIndex === null ) return;
            const card = grid.querySelector( `.tc-card[data-index="${ activeIndex }"]` );
            if ( card ) {
                card.classList.remove( 'tc-card--active' );
                card.setAttribute( 'aria-expanded', 'false' );
            }
            bioPanel.setAttribute( 'hidden', '' );
            activeIndex = null;
        }

        // ── Card click handler ─────────────────────────────────────────
        cards.forEach( ( card ) => {
            const idx = parseInt( card.dataset.index, 10 );

            function handleActivate() {
                if ( activeIndex === idx ) {
                    closePanel();
                } else {
                    openCard( idx );
                }
            }

            card.addEventListener( 'click', handleActivate );
            card.addEventListener( 'keydown', ( e ) => {
                if ( e.key === 'Enter' || e.key === ' ' ) {
                    e.preventDefault();
                    handleActivate();
                }
            } );
        } );

        // ── Close button ───────────────────────────────────────────────
        if ( closeBtn ) {
            closeBtn.addEventListener( 'click', closePanel );
        }

        // ── Reposition panel on resize (row count can change) ─────────
        window.addEventListener( 'resize', () => {
            if ( activeIndex !== null ) {
                insertPanelAfterRow( activeIndex );
            }
        } );

        // ── Open first card on load if configured ─────────────────────
        if ( showDefault && cards.length > 0 ) {
            const firstIdx = parseInt( cards[ 0 ].dataset.index, 10 );
            openCard( firstIdx, true ); // silent — don't scroll on page load
        }
    }

    // ── Boot ───────────────────────────────────────────────────────────
    function init() {
        document.querySelectorAll( '.tc-block' ).forEach( initBlock );
    }

    if ( document.readyState === 'loading' ) {
        document.addEventListener( 'DOMContentLoaded', init );
    } else {
        init();
    }

} )();
